
import React, { useState, useEffect } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Grid, Typography, Avatar, Chip, Button, IconButton,
  Divider, CircularProgress, TextField, MenuItem,
  Checkbox, Tooltip, Table, TableHead, TableBody,
  TableRow, TableCell, Alert,
} from "@mui/material";
import {
  Close as CloseIcon, Add, Timeline, FolderOpen,
  CheckCircle, RadioButtonUnchecked, Delete,
  UploadFile, Visibility as ViewIcon, InsertDriveFile,
  FileDownload,
} from "@mui/icons-material";
import LinearProgress from "@mui/material/LinearProgress";
import {
  STATUS_COLOR, DEPT_COLOR, DOC_STATUS_COLOR, BGV_STATUS_COLOR,
  CURRENCIES, DOCUMENT_CATEGORIES, DOCUMENT_STATUSES,
  BGV_STATUSES, BLOOD_GROUPS, EMPTY_ENG,
  fmtDate, fmtMoney, nameInitials, errText,
} from "./employeeConstants";
import {
  FONT_BODY, FONT_DISPLAY, COLORS, TYPE, RADIUS, CONTROL_HEIGHT, ELLIPSIS, humanizeStatus,
} from "../../../styles/recruiterTheme";

// ── API helpers (onboarding + engagement) ────────────────────────────────────
const BASE = process.env.REACT_APP_API_BASE_URL;
const EMPLOYEE_BASE = process.env.REACT_APP_API_EMPLOYEES_URL;
const ONBOARDING_BASE = process.env.REACT_APP_API_ONBOARDING_URL;
const hdrs = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
});
const CLIENT_BASE = process.env.REACT_APP_API_CLIENTS_URL;
// Tolerates empty / non-JSON bodies (e.g. a 204 on DELETE or an HTML 502 page).
const ok = async (res) => {
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw (data && Object.keys(data).length ? data : new Error(`Request failed (${res.status})`));
  return data;
};

// Shared button / dialog styling.
const btnSx = { height: CONTROL_HEIGHT.md, textTransform: "none", fontWeight: 600, borderRadius: `${RADIUS.sm}px`, px: 2 };
const primaryBtnSx = { ...btnSx, bgcolor: COLORS.primary, "&:hover": { bgcolor: COLORS.primaryDark } };
const smallBtnSx = { textTransform: "none", fontWeight: 600, fontSize: TYPE.caption, height: 28, borderRadius: `${RADIUS.sm}px` };
const headSx = {
  fontSize: TYPE.overline, fontWeight: 700, color: COLORS.muted, textTransform: "uppercase",
  letterSpacing: "0.06em", whiteSpace: "nowrap", bgcolor: COLORS.surfaceTint,
};
const fieldLabelSx = { fontSize: TYPE.overline, color: COLORS.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em" };

const api = {
  getOnboarding:    (id)          => fetch(`${ONBOARDING_BASE}/${id}`,                  { headers: hdrs() }).then(ok),
  updateOnboarding: (id, pl)      => fetch(`${ONBOARDING_BASE}/${id}`,                  { method: "PUT",    headers: hdrs(), body: JSON.stringify(pl) }).then(ok),
  toggleChecklist:  (id, idx, pl) => fetch(`${ONBOARDING_BASE}/${id}/checklist/${idx}`, { method: "PUT",    headers: hdrs(), body: JSON.stringify(pl) }).then(ok),
  addDocument:      (id, pl)      => fetch(`${ONBOARDING_BASE}/${id}/document`,          { method: "POST",   headers: hdrs(), body: JSON.stringify(pl) }).then(ok),
  updateDocument:   (id, idx, pl) => fetch(`${ONBOARDING_BASE}/${id}/document/${idx}`,  { method: "PUT",    headers: hdrs(), body: JSON.stringify(pl) }).then(ok),
  deleteDocument:   (id, idx)     => fetch(`${ONBOARDING_BASE}/${id}/document/${idx}`,  { method: "DELETE", headers: hdrs() }).then(ok),
 
  getLifecycle: (id)     => fetch(`${EMPLOYEE_BASE}/${id}/lifecycle`, { headers: hdrs() }).then(ok),
  updateSalary: (id, pl) => fetch(`${EMPLOYEE_BASE}/${id}/salary`,    { method: "POST", headers: hdrs(), body: JSON.stringify(pl) }).then(ok),
 
  // File upload — FormData, no Content-Type (browser sets multipart boundary)
  uploadFile: (id, idx, file) => {
    const fd = new FormData();
    fd.append("file", file);
    return fetch(`${ONBOARDING_BASE}/${id}/document/${idx}/upload`, {
      method: "POST",
      headers: { Authorization: `Bearer ${localStorage.getItem("token") || ""}` },
      body: fd,
    }).then(ok);
  },
  // Fetch file as blob → object URL for inline viewing in new tab
  viewFile: async (id, idx) => {
    const res = await fetch(`${ONBOARDING_BASE}/${id}/document/${idx}/file`, { headers: hdrs() });
    if (!res.ok) throw new Error("File not found");
    const blob = await res.blob();
    return URL.createObjectURL(blob);
  },
  addEngagement: (id, pl)    => fetch(`${EMPLOYEE_BASE}/${id}/engagement`,         { method: "POST", headers: hdrs(), body: JSON.stringify(pl) }).then(ok),
  endEngagement: (id, idx)   => fetch(`${EMPLOYEE_BASE}/${id}/engagement/${idx}/end`,  { method: "PUT",  headers: hdrs(), body: JSON.stringify({}) }).then(ok),
  // Export single employee full profile as Excel
  exportEmployee: async (id, fileName) => {
    const res = await fetch(`${BASE}/export/employee/${id}/excel`, { headers: hdrs() });
    if (!res.ok) throw new Error("Export failed");
    const blob = await res.blob();
    const link = document.createElement("a");
    link.href     = URL.createObjectURL(blob);
    link.download = fileName;
    link.click();
    setTimeout(() => URL.revokeObjectURL(link.href), 1000);
  },
};

// ── Section label helper ──────────────────────────────────────────────────────
const SectionLabel = ({ children }) => (
  <Typography fontSize={TYPE.overline} fontWeight={700} color={COLORS.muted}
    textTransform="uppercase" letterSpacing="0.06em" mb={1.5}>
    {children}
  </Typography>
);

// ────────────────────────────────────────────────────────────────────────────
// TAB 0 — Profile
// ────────────────────────────────────────────────────────────────────────────
function ProfileTab({ employee }) {
  const dc = DEPT_COLOR[employee.department] || "#475569";

  const infoRows = [
    ["Email",           employee.email           || "—"],
    ["Phone",           employee.phone           || "—"],
    ["Location",        employee.location        || "—"],
    ["Experience",      employee.experience || employee.experience === 0 ? `${employee.experience} years` : "—"],
    ["Date of Joining", fmtDate(employee.date_of_joining)],
    ["Reporting To",    employee.reporting_manager || "—"],
  ];

  return (
    <Box p={3}>
      <SectionLabel>Personal &amp; Contact</SectionLabel>
      <Grid container spacing={2} mb={3}>
        {infoRows.map(([label, val]) => (
          <Grid item xs={6} sm={4} key={label}>
            <Typography sx={fieldLabelSx}>{label}</Typography>
            <Typography fontWeight={600} fontSize={13}>{val}</Typography>
          </Grid>
        ))}
      </Grid>

      <Divider sx={{ my: 2 }} />
      <SectionLabel>Current Deployment</SectionLabel>
      <Box p={2} borderRadius={2} mb={3}
        sx={{ bgcolor: employee.current_client ? COLORS.successSoft : COLORS.surfaceTint,
              border: `1px solid ${employee.current_client ? "#bbf7d0" : COLORS.border}` }}>
        {employee.current_client ? (
          <Grid container spacing={2}>
            {[
              ["Client",       employee.current_client,                                            COLORS.success],
              ["Project",      employee.current_project || "—",                                    null],
              ["Billing Rate", fmtMoney(employee.current_billing_rate, employee.billing_currency), COLORS.success],
              ["Currency",     employee.billing_currency,                                          null],
            ].map(([label, val, color]) => (
              <Grid item xs={6} sm={3} key={label}>
                <Typography sx={fieldLabelSx}>{label}</Typography>
                <Typography fontWeight={700} fontSize={14} color={color || "text.primary"}>{val}</Typography>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Typography fontSize={13} color="text.secondary" textAlign="center" py={1}>
            Not currently deployed to any client
          </Typography>
        )}
      </Box>

      <Divider sx={{ my: 2 }} />
      <SectionLabel>Compensation</SectionLabel>
      <Grid container spacing={2} mb={2}>
        <Grid item xs={6} sm={4}>
          <Typography sx={fieldLabelSx}>Annual Salary</Typography>
          <Typography fontWeight={700} fontSize={14}>{fmtMoney(employee.salary)}</Typography>
        </Grid>
      </Grid>

      {employee.skills && (
        <>
          <Divider sx={{ my: 2 }} />
          <SectionLabel>Skills</SectionLabel>
          <Box display="flex" flexWrap="wrap" gap={0.8}>
            {String(employee.skills).split(",").map(x => x.trim()).filter(Boolean).map((s, i) => (
              <Chip key={i} label={s.trim()} size="small" variant="outlined"
                sx={{ fontSize: 11, borderColor: dc, color: dc }} />
            ))}
          </Box>
        </>
      )}

      {employee.notes && (
        <Box mt={2} p={1.5} bgcolor={COLORS.surfaceTint} borderRadius={2}>
          <Typography sx={fieldLabelSx} mb={0.3}>Notes</Typography>
          <Typography fontSize={13}>{employee.notes}</Typography>
        </Box>
      )}
    </Box>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// TAB 1 — Onboarding
// ────────────────────────────────────────────────────────────────────────────
function OnboardingTab({ employee, onboarding, onRefresh }) {
  const [saving, setSaving] = useState(false);
  const [form,   setForm]   = useState({});
  const [err,    setErr]    = useState("");
  const [saved,  setSaved]  = useState(false);
  const [toggling, setToggling] = useState(null);   // checklist index in flight

  useEffect(() => {
    if (onboarding) {
      setForm({
        blood_group:        onboarding.blood_group        || "",
        personal_email:     onboarding.personal_email     || "",
        referred_by:        onboarding.referred_by        || "",
        bgv_status:         onboarding.bgv_status         || "Not Initiated",
        bgv_agency:         onboarding.bgv_agency         || "",
        bgv_remarks:        onboarding.bgv_remarks        || "",
        laptop_serial:      onboarding.laptop_serial      || "",
        laptop_make_model:  onboarding.laptop_make_model  || "",
        access_card_number: onboarding.access_card_number || "",
        email_id_created:   onboarding.email_id_created   || "",
        hr_notes:           onboarding.hr_notes           || "",
        it_notes:           onboarding.it_notes           || "",
        probation_end_date: onboarding.probation_end_date
          ? onboarding.probation_end_date.split("T")[0] : "",
        bank_details:      onboarding.bank_details      || {},
        emergency_contact: onboarding.emergency_contact || {},
      });
    }
  }, [onboarding]);

  const handle    = (e) => setForm(p => ({ ...p, [e.target.name]: e.target.value }));
  const handleBD  = (e) => setForm(p => ({ ...p, bank_details:      { ...p.bank_details,      [e.target.name]: e.target.value } }));
  const handleEC  = (e) => setForm(p => ({ ...p, emergency_contact: { ...p.emergency_contact, [e.target.name]: e.target.value } }));

  const save = async () => {
    if (saving) return;
    setSaving(true); setErr(""); setSaved(false);
    try {
      await api.updateOnboarding(employee._id, form);
      setSaved(true);
      onRefresh();
    } catch (e) {
      setErr(errText(e, "Could not save onboarding data"));
    } finally { setSaving(false); }
  };

  const handleToggle = async (idx, item) => {
    if (toggling !== null) return;
    setToggling(idx); setErr("");
    try {
      await api.toggleChecklist(employee._id, idx, { done: !item.done, remarks: item.remarks });
      await onRefresh();
    } catch (e) {
      setErr(errText(e, "Could not update checklist item"));
    } finally { setToggling(null); }
  };

  if (!onboarding) return null;   // parent shows loading / error + retry

  const checklist = onboarding.checklist || [];
  const doneCount = checklist.filter(i => i.done).length;
  const pct       = checklist.length ? Math.round((doneCount / checklist.length) * 100) : 0;
  const bd        = form.bank_details      || {};
  const ec        = form.emergency_contact || {};

  return (
    <Box p={3}>
      {err && <Alert severity="error" onClose={() => setErr("")} sx={{ mb: 2 }}>{err}</Alert>}

      {/* ── Checklist ─────────────────────────────────────────────────────── */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
        <SectionLabel>Onboarding Checklist — {doneCount}/{checklist.length} Complete</SectionLabel>
        <Chip label={`${pct}%`} size="small"
          color={pct === 100 ? "success" : pct >= 50 ? "warning" : "default"}
          sx={{ fontWeight: 700 }} />
      </Box>

      <Box mb={3} sx={{ border: `1px solid ${COLORS.border}`, borderRadius: 2, overflow: "hidden" }}>
        {checklist.map((item, idx) => (
          <Box key={idx} display="flex" alignItems="center" gap={1.5} px={2} py={1.2}
            sx={{ borderBottom: idx < checklist.length - 1 ? `1px solid ${COLORS.border}` : "none",
                  bgcolor: item.done ? COLORS.successSoft : "transparent", transition: "background 0.15s" }}>
            <Checkbox
              checked={!!item.done}
              disabled={toggling !== null}
              onChange={() => handleToggle(idx, item)}
              icon={<RadioButtonUnchecked sx={{ color: COLORS.faint }} />}
              checkedIcon={<CheckCircle sx={{ color: COLORS.success }} />}
              size="small" sx={{ p: 0 }}
            />
            <Typography fontSize={13} flex={1} fontWeight={item.done ? 600 : 400}
              color={item.done ? COLORS.success : "text.primary"}>
              {item.label}
            </Typography>
            {toggling === idx ? <CircularProgress size={14} /> : item.done && (
              <Typography fontSize={TYPE.overline} color={COLORS.faint} sx={{ whiteSpace: "nowrap" }}>{fmtDate(item.updated_at)}</Typography>
            )}
          </Box>
        ))}
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* ── Personal Info ─────────────────────────────────────────────────── */}
      <SectionLabel>Personal Info</SectionLabel>
      <Grid container spacing={2} mb={2}>
        <Grid item xs={12} sm={4}>
          <TextField select fullWidth size="small" label="Blood Group" name="blood_group"
            value={form.blood_group || ""} onChange={handle}>
            <MenuItem value="">—</MenuItem>
            {BLOOD_GROUPS.map(g => <MenuItem key={g} value={g}>{g}</MenuItem>)}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField fullWidth size="small" type="email" label="Personal Email"
            name="personal_email" value={form.personal_email || ""} onChange={handle} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField fullWidth size="small" label="Referred By"
            name="referred_by" value={form.referred_by || ""} onChange={handle} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField fullWidth size="small" type="date" label="Probation End Date"
            name="probation_end_date" value={form.probation_end_date || ""} onChange={handle}
            InputLabelProps={{ shrink: true }} />
        </Grid>
      </Grid>

      <Divider sx={{ my: 2 }} />

      {/* ── IT Assets ─────────────────────────────────────────────────────── */}
      <SectionLabel>IT Assets &amp; Access</SectionLabel>
      <Grid container spacing={2} mb={2}>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth size="small" label="Laptop Serial Number"
            name="laptop_serial" value={form.laptop_serial || ""} onChange={handle} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth size="small" label="Laptop Make / Model"
            name="laptop_make_model" value={form.laptop_make_model || ""} onChange={handle} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth size="small" label="Access Card Number"
            name="access_card_number" value={form.access_card_number || ""} onChange={handle} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth size="small" label="Corporate Email Created"
            name="email_id_created" value={form.email_id_created || ""} onChange={handle} />
        </Grid>
      </Grid>

      <Divider sx={{ my: 2 }} />

      {/* ── Bank Details ──────────────────────────────────────────────────── */}
      <SectionLabel>Bank Details</SectionLabel>
      <Grid container spacing={2} mb={2}>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth size="small" label="Account Holder Name"
            name="account_holder_name" value={bd.account_holder_name || ""} onChange={handleBD} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth size="small" label="Account Number"
            name="account_number" value={bd.account_number || ""} onChange={handleBD} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField fullWidth size="small" label="IFSC Code"
            name="ifsc_code" value={bd.ifsc_code || ""} onChange={handleBD} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField fullWidth size="small" label="Bank Name"
            name="bank_name" value={bd.bank_name || ""} onChange={handleBD} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField fullWidth size="small" label="Branch"
            name="branch" value={bd.branch || ""} onChange={handleBD} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField select fullWidth size="small" label="Account Type" name="account_type"
            value={bd.account_type || "Savings"} onChange={handleBD}>
            {["Savings", "Current", "NRE", "NRO"].map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
          </TextField>
        </Grid>
      </Grid>

      <Divider sx={{ my: 2 }} />

      {/* ── Emergency Contact ─────────────────────────────────────────────── */}
      <SectionLabel>Emergency Contact</SectionLabel>
      <Grid container spacing={2} mb={2}>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth size="small" label="Contact Name"
            name="name" value={ec.name || ""} onChange={handleEC} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth size="small" label="Relationship"
            name="relationship" value={ec.relationship || ""} onChange={handleEC} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth size="small" label="Phone"
            name="phone" value={ec.phone || ""} onChange={handleEC} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth size="small" type="email" label="Email"
            name="email" value={ec.email || ""} onChange={handleEC} />
        </Grid>
        <Grid item xs={12}>
          <TextField fullWidth size="small" label="Address"
            name="address" value={ec.address || ""} onChange={handleEC} />
        </Grid>
      </Grid>

      <Divider sx={{ my: 2 }} />

      {/* ── BGV ───────────────────────────────────────────────────────────── */}
      <SectionLabel>Background Verification</SectionLabel>
      <Grid container spacing={2} mb={2}>
        <Grid item xs={12} sm={4}>
          <TextField select fullWidth size="small" label="BGV Status"
            name="bgv_status" value={form.bgv_status || ""} onChange={handle}>
            {BGV_STATUSES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField fullWidth size="small" label="BGV Agency"
            name="bgv_agency" value={form.bgv_agency || ""} onChange={handle} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <Box mt={0.5}>
            <Typography fontSize={11} color="text.secondary" mb={0.8}>Current BGV Status</Typography>
            <Chip label={form.bgv_status || "Not Initiated"}
              color={BGV_STATUS_COLOR[form.bgv_status] || "default"} size="small" sx={{ fontWeight: 700 }} />
          </Box>
        </Grid>
        <Grid item xs={12}>
          <TextField fullWidth size="small" label="BGV Remarks"
            name="bgv_remarks" value={form.bgv_remarks || ""} onChange={handle} />
        </Grid>
      </Grid>

      <Divider sx={{ my: 2 }} />

      {/* ── Internal Notes ────────────────────────────────────────────────── */}
      <SectionLabel>Internal Notes</SectionLabel>
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth multiline rows={2} size="small" label="HR Notes"
            name="hr_notes" value={form.hr_notes || ""} onChange={handle} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth multiline rows={2} size="small" label="IT Notes"
            name="it_notes" value={form.it_notes || ""} onChange={handle} />
        </Grid>
      </Grid>

      <Box display="flex" alignItems="center" gap={2}>
        <Button variant="contained" onClick={save} disabled={saving} sx={primaryBtnSx}>
          {saving && <CircularProgress size={16} sx={{ mr: 1, color: "#fff" }} />}
          Save Onboarding Data
        </Button>
        {saved && !saving && (
          <Typography fontSize={TYPE.small} color={COLORS.success} fontWeight={600}>Saved</Typography>
        )}
      </Box>
    </Box>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// TAB 2 — Documents  (with upload + inline view)
// ────────────────────────────────────────────────────────────────────────────
const EMPTY_DOC = { name: "", category: "Identity", status: "Pending", remarks: "" };
const ACCEPTED  = ".pdf,.png,.jpg,.jpeg,.doc,.docx,.xls,.xlsx,.txt";
const TF        = { width: "100%", minWidth: 250 };

function DocumentsTab({ employee, onboarding, onRefresh }) {
  const [addOpen,     setAddOpen]     = useState(false);
  const [form,        setForm]        = useState(EMPTY_DOC);
  const [pendingFile, setPendingFile] = useState(null);   // file chosen in dialog
  const [saving,      setSaving]      = useState(false);
  const [uploading,   setUploading]   = useState(null);
  const [viewing,     setViewing]     = useState(null);
  const [viewerOpen,  setViewerOpen]  = useState(false);
  const [viewerUrl,   setViewerUrl]   = useState("");
  const [viewerName,  setViewerName]  = useState("");
  const [err,         setErr]         = useState("");   // row-level failures
  const [dialogErr,   setDialogErr]   = useState("");   // add-document dialog failures
  const [busyIdx,     setBusyIdx]     = useState(null); // status change / delete in flight
  const dialogFileRef = React.useRef(null);
  const fileRefs      = React.useRef({});

  const documents = onboarding?.documents || [];
  const handle    = e => setForm(p => ({ ...p, [e.target.name]: e.target.value }));

  const resetDialog = () => { setAddOpen(false); setForm(EMPTY_DOC); setPendingFile(null); setDialogErr(""); };

  // ── Add document entry + optional immediate upload ────────────────────────
  const handleAdd = async () => {
    if (saving || !form.name.trim() || form.name === "__custom__") return;
    setSaving(true); setDialogErr("");
    let added = false;
    try {
      const res      = await api.addDocument(employee._id, { ...form, name: form.name.trim() });
      added = true;
      const newIdx   = (res.data?.documents?.length ?? 1) - 1;
      // If user picked a file in the dialog, upload it straight away
      if (pendingFile && newIdx >= 0) {
        await api.uploadFile(employee._id, newIdx, pendingFile);
      }
      onRefresh(); resetDialog();
    } catch (e) {
      if (added) {
        // Entry exists but the file failed — close and point at the row's upload button.
        onRefresh(); resetDialog();
        setErr(`Document added, but the file upload failed: ${errText(e, "upload error")}. Use the upload button on the row to retry.`);
      } else {
        setDialogErr(errText(e, "Could not add document"));
      }
    } finally { setSaving(false); }
  };

  // ── Status change in row ──────────────────────────────────────────────────
  const handleStatusChange = async (idx, status) => {
    setBusyIdx(idx); setErr("");
    try {
      await api.updateDocument(employee._id, idx, { status });
      await onRefresh();
    } catch (e) {
      setErr(errText(e, "Could not update document status"));
    } finally { setBusyIdx(null); }
  };

  // ── Delete entry + file ───────────────────────────────────────────────────
  const handleDelete = async (idx) => {
    if (!window.confirm("Remove this document and its uploaded file?")) return;
    setBusyIdx(idx); setErr("");
    try {
      await api.deleteDocument(employee._id, idx);
      await onRefresh();
    } catch (e) {
      setErr(errText(e, "Could not remove document"));
    } finally { setBusyIdx(null); }
  };

  // ── Upload file for existing row ──────────────────────────────────────────
  const handleFileChange = async (idx, e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(idx); setErr("");
    try {
      await api.uploadFile(employee._id, idx, file);
      onRefresh();
    } catch (e) {
      setErr(errText(e, "Upload failed"));
    } finally {
      setUploading(null);
      e.target.value = "";
    }
  };

  // ── View uploaded file ────────────────────────────────────────────────────
  const handleView = async (idx, fileName) => {
    setViewing(idx); setErr("");
    try {
      const objectUrl = await api.viewFile(employee._id, idx);
      const ext = String(fileName || "").split(".").pop().toLowerCase();
      if (["pdf", "png", "jpg", "jpeg"].includes(ext)) {
        setViewerUrl(objectUrl); setViewerName(fileName); setViewerOpen(true);
      } else {
        const link = document.createElement("a");
        link.href = objectUrl; link.download = fileName; link.click();
        setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
      }
    } catch {
      setErr("Could not load file. It may have been deleted from the server.");
    } finally {
      setViewing(null);
    }
  };

  const closeViewer = () => {
    URL.revokeObjectURL(viewerUrl);
    setViewerUrl(""); setViewerName(""); setViewerOpen(false);
  };

  // ── Group by category ─────────────────────────────────────────────────────
  const grouped = Object.keys(DOCUMENT_CATEGORIES).reduce((acc, cat) => {
    const docs = documents.map((d, idx) => ({ ...d, idx })).filter(d => d.category === cat);
    if (docs.length) acc[cat] = docs;
    return acc;
  }, {});
  const uncategorized = documents.map((d, idx) => ({ ...d, idx }))
    .filter(d => !Object.keys(DOCUMENT_CATEGORIES).includes(d.category));
  if (uncategorized.length) grouped["Uncategorized"] = uncategorized;

  const stats = {
    total:    documents.length,
    verified: documents.filter(d => d.status === "Verified").length,
    pending:  documents.filter(d => d.status === "Pending").length,
  };

  return (
    <Box p={3}>
      {err && <Alert severity="error" onClose={() => setErr("")} sx={{ mb: 2 }}>{err}</Alert>}

      {/* ── Header ──────────────────────────────────────────────────────── */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box display="flex" alignItems="center" gap={1.5}>
          <SectionLabel>Documents</SectionLabel>
          <Box display="flex" gap={1} mb={1.5}>
            <Chip label={`${stats.verified} Verified`} size="small" color="success" sx={{ fontWeight: 600 }} />
            <Chip label={`${stats.pending} Pending`}   size="small" color="warning" sx={{ fontWeight: 600 }} />
            <Chip label={`${stats.total} Total`}       size="small"                 sx={{ fontWeight: 600 }} />
          </Box>
        </Box>
        <Button size="small" variant="outlined" startIcon={<Add />} onClick={() => setAddOpen(true)}
          sx={{ ...btnSx, height: CONTROL_HEIGHT.sm, borderColor: COLORS.primary, color: COLORS.primary, mb: 1.5 }}>
          Add Document
        </Button>
      </Box>

      {/* ── Empty state ──────────────────────────────────────────────────── */}
      {documents.length === 0 ? (
        <Box display="flex" flexDirection="column" alignItems="center" py={6} gap={1}>
          <FolderOpen sx={{ fontSize: 48, color: COLORS.faint }} />
          <Typography color="text.secondary" fontWeight={600}>No documents added yet</Typography>
          <Typography fontSize={13} color="text.disabled">
            Click "Add Document" to add and upload employee documents.
          </Typography>
        </Box>
      ) : (
        Object.entries(grouped).map(([cat, docs]) => (
          <Box key={cat} mb={3}>
            <Typography fontSize={11} fontWeight={700} color="text.secondary"
              textTransform="uppercase" mb={1}>{cat}</Typography>
            <Box sx={{ border: `1px solid ${COLORS.border}`, borderRadius: 2, overflow: "hidden" }}>
              {docs.map((doc, i) => {
                const hasFile     = !!doc.file_name;
                const isUploading = uploading === doc.idx;
                const isViewing   = viewing   === doc.idx;
                const ext         = (doc.file_name || "").split(".").pop().toLowerCase();
                const isPreview   = ["pdf", "png", "jpg", "jpeg"].includes(ext);

                return (
                  <Box key={doc.idx} sx={{ borderBottom: i < docs.length - 1 ? `1px solid ${COLORS.border}` : "none" }}>
                    <Box display="flex" alignItems="center" gap={1.5} px={2} py={1.5}
                      sx={{ "&:hover": { bgcolor: COLORS.surfaceTint } }}>

                      {/* Doc name + file badge */}
                      <Box flex={1} minWidth={0}>
                        <Typography fontSize={13} fontWeight={500} noWrap>{doc.name}</Typography>
                        {hasFile && (
                          <Box display="flex" alignItems="center" gap={0.5} mt={0.3}>
                            <InsertDriveFile sx={{ fontSize: 11, color: COLORS.muted }} />
                            <Typography fontSize={11} color="text.secondary" noWrap sx={{ maxWidth: 200 }}>
                              {doc.file_name}
                            </Typography>
                          </Box>
                        )}
                      </Box>

                      {/* Status selector */}
                      <TextField select size="small" value={doc.status || "Pending"}
                        disabled={busyIdx === doc.idx}
                        onChange={e => handleStatusChange(doc.idx, e.target.value)}
                        sx={{ width: 130, minWidth: 120 }}>
                        {DOCUMENT_STATUSES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                      </TextField>

                      <Chip label={doc.status} size="small"
                        color={DOC_STATUS_COLOR[doc.status] || "default"}
                        sx={{ fontWeight: 600, minWidth: 74 }} />

                      <Typography fontSize={11} color="text.disabled" sx={{ minWidth: 82 }}>
                        {fmtDate(doc.updated_at)}
                      </Typography>

                      {/* View */}
                      {hasFile ? (
                        <Tooltip title={isPreview ? "View file" : "Download file"}>
                          <IconButton size="small" color="primary"
                            onClick={() => handleView(doc.idx, doc.file_name)} disabled={isViewing}>
                            {isViewing ? <CircularProgress size={16} /> : <ViewIcon fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                      ) : <Box sx={{ width: 30 }} />}

                      {/* Upload */}
                      <Tooltip title={hasFile ? "Replace file" : "Upload file"}>
                        <span>
                          <IconButton size="small"
                            sx={{ color: hasFile ? COLORS.success : COLORS.muted }}
                            onClick={() => fileRefs.current[doc.idx]?.click()}
                            disabled={isUploading}>
                            {isUploading ? <CircularProgress size={16} /> : <UploadFile fontSize="small" />}
                          </IconButton>
                        </span>
                      </Tooltip>
                      <input type="file" accept={ACCEPTED} style={{ display: "none" }}
                        ref={el => fileRefs.current[doc.idx] = el}
                        onChange={e => handleFileChange(doc.idx, e)} />

                      {/* Delete */}
                      <Tooltip title="Remove document">
                        <IconButton size="small" color="error" onClick={() => handleDelete(doc.idx)} disabled={busyIdx === doc.idx}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    {isUploading && <LinearProgress sx={{ height: 2, mx: 2, mb: 1, borderRadius: 1 }} />}
                  </Box>
                );
              })}
            </Box>
          </Box>
        ))
      )}

      {/* ── Add Document Dialog ──────────────────────────────────────────── */}
      <Dialog open={addOpen} onClose={resetDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ borderBottom: `1px solid ${COLORS.border}` }}>
          Add Document
        </DialogTitle>
        <DialogContent sx={{ pt: "24px !important" }}>
          {dialogErr && <Alert severity="error" sx={{ mb: 2 }}>{dialogErr}</Alert>}
          <Grid container spacing={2}>

            {/* Category */}
            <Grid item xs={12} sm={6}>
              <TextField
                select size="small" label="Category" name="category"
                value={form.category} sx={TF}
                onChange={e => setForm(p => ({ ...p, category: e.target.value, name: "" }))}>
                {Object.keys(DOCUMENT_CATEGORIES).map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </TextField>
            </Grid>

            {/* Document name */}
            <Grid item xs={12} sm={6}>
              <TextField
                select size="small" label="Document Name" name="name"
                value={form.name} onChange={handle} sx={TF}>
                <MenuItem value="">— Select document —</MenuItem>
                {(DOCUMENT_CATEGORIES[form.category] || []).map(d => (
                  <MenuItem key={d} value={d}>{d}</MenuItem>
                ))}
                <MenuItem value="__custom__">Custom (type below)…</MenuItem>
              </TextField>
            </Grid>

            {/* Custom name input */}
            {form.name === "__custom__" && (
              <Grid item xs={12}>
                <TextField
                  size="small" label="Custom Document Name"
                  placeholder="Enter document name"
                  onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                  sx={TF}
                />
              </Grid>
            )}

            {/* Status */}
            <Grid item xs={12} sm={6}>
              <TextField
                select size="small" label="Submission Status" name="status"
                value={form.status} onChange={handle} sx={TF}>
                {DOCUMENT_STATUSES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </TextField>
            </Grid>

            {/* Remarks */}
            <Grid item xs={12}>
              <TextField
                size="small" label="Remarks (optional)" name="remarks"
                value={form.remarks} onChange={handle}
                placeholder="Any notes about this document…" sx={TF}
              />
            </Grid>

            {/* ── File upload inside dialog ────────────────────────────── */}
            <Grid item xs={12}>
              <Typography fontSize={11} fontWeight={700} color="text.secondary"
                textTransform="uppercase" mb={1}>
                Upload File (Optional)
              </Typography>
              <Box
                onClick={() => dialogFileRef.current?.click()}
                sx={{
                  border: `2px dashed ${pendingFile ? COLORS.success : COLORS.faint}`,
                  borderRadius: 2,
                  p: 2.5,
                  textAlign: "center",
                  cursor: "pointer",
                  bgcolor: pendingFile ? COLORS.successSoft : COLORS.surfaceTint,
                  transition: "all 0.2s",
                  "&:hover": { borderColor: COLORS.primary, bgcolor: COLORS.accentSoft },
                }}>
                {pendingFile ? (
                  <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                    <InsertDriveFile sx={{ color: COLORS.success }} />
                    <Box textAlign="left">
                      <Typography fontSize={13} fontWeight={600} color={COLORS.success}>
                        {pendingFile.name}
                      </Typography>
                      <Typography fontSize={11} color="text.secondary">
                        {(pendingFile.size / 1024).toFixed(1)} KB · Click to change
                      </Typography>
                    </Box>
                  </Box>
                ) : (
                  <Box>
                    <UploadFile sx={{ fontSize: 32, color: COLORS.faint, mb: 0.5 }} />
                    <Typography fontSize={13} fontWeight={600} color="text.secondary">
                      Click to browse and select a file
                    </Typography>
                    <Typography fontSize={11} color="text.disabled" mt={0.3}>
                      PDF, PNG, JPG, DOC, DOCX, XLS, XLSX, TXT
                    </Typography>
                  </Box>
                )}
              </Box>
              <input
                type="file" accept={ACCEPTED} style={{ display: "none" }}
                ref={dialogFileRef}
                onChange={e => { setPendingFile(e.target.files[0] || null); e.target.value = ""; }}
              />
              {pendingFile && (
                <Button size="small" onClick={() => setPendingFile(null)}
                  sx={{ mt: 0.5, fontSize: 11, color: COLORS.muted, textTransform: "none" }}>
                  Remove file
                </Button>
              )}
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${COLORS.border}` }}>
          <Button onClick={resetDialog} disabled={saving} sx={{ ...btnSx, color: COLORS.muted }}>Cancel</Button>
          <Button variant="contained" onClick={handleAdd}
            disabled={saving || !form.name.trim() || form.name === "__custom__"}
            sx={primaryBtnSx}>
            {saving && <CircularProgress size={16} sx={{ mr: 1, color: "#fff" }} />}
            {pendingFile ? "Add & Upload" : "Add Document"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── File Viewer Dialog (PDF / Image) ────────────────────────────── */}
      <Dialog open={viewerOpen} onClose={closeViewer} maxWidth="lg" fullWidth
        PaperProps={{ sx: { height: "90vh" } }}>
        <DialogTitle sx={{ fontWeight: 700, borderBottom: `1px solid ${COLORS.border}`, py: 1.5 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center" gap={1}>
              <InsertDriveFile sx={{ color: COLORS.primary }} />
              <Typography fontWeight={700} fontSize={14}>{viewerName}</Typography>
            </Box>
            <Box display="flex" gap={1}>
              <Button size="small" variant="outlined" sx={{ textTransform: "none", fontSize: 12 }}
                onClick={() => {
                  const a = document.createElement("a");
                  a.href = viewerUrl; a.download = viewerName; a.click();
                }}>
                Download
              </Button>
              <IconButton size="small" onClick={closeViewer}><CloseIcon fontSize="small" /></IconButton>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0, display: "flex", flexDirection: "column" }}>
          {viewerUrl && (() => {
            const ext = viewerName.split(".").pop().toLowerCase();
            if (ext === "pdf") return (
              <iframe src={viewerUrl} title={viewerName}
                style={{ flex: 1, border: "none", width: "100%", height: "100%" }} />
            );
            if (["png", "jpg", "jpeg"].includes(ext)) return (
              <Box display="flex" justifyContent="center" alignItems="center"
                flex={1} p={2} bgcolor={COLORS.surfaceTint}>
                <img src={viewerUrl} alt={viewerName}
                  style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", borderRadius: 8 }} />
              </Box>
            );
            return null;
          })()}
        </DialogContent>
      </Dialog>
    </Box>
  );
}


// TAB 3 — Client History / Engagements

function EngagementTab({ employee, onRefresh }) {
  const [open,          setOpen]          = useState(false);
  const [editOpen,      setEditOpen]      = useState(false);
  const [editIdx,       setEditIdx]       = useState(null);
  const [editForm,      setEditForm]      = useState({});
  const [billingOpen,   setBillingOpen]   = useState(false);
  const [billingIdx,    setBillingIdx]    = useState(null);
  const [billingForm,   setBillingForm]   = useState({ billing_rate: "", billing_currency: "INR", effective_from: "", note: "" });
  const [form,          setForm]          = useState(EMPTY_ENG);
  const [saving,        setSaving]        = useState(false);
  const [clientNames,   setClientNames]   = useState([]);
  const [err,           setErr]           = useState("");   // page-level (End) failures
  const [dialogErr,     setDialogErr]     = useState("");   // add / edit / billing dialog failures
  const [ending,        setEnding]        = useState(null); // engagement index being ended

  const dc          = DEPT_COLOR[employee.department] || "#475569";
  const history     = Array.isArray(employee.client_history) ? employee.client_history : [];

  useEffect(() => {
    fetch(`${CLIENT_BASE}/names/list`, { headers: hdrs() })
      .then(r => r.json())
      .then(d => { if (d?.success && Array.isArray(d.data)) setClientNames(d.data); })
      .catch(() => setClientNames(["ZentreeLabs Pvt Ltd"]));
  }, []);

  // Keep a saved client selectable even if it is no longer in the clients list.
  const withClient = (name) => (name && !clientNames.includes(name) ? [name, ...clientNames] : clientNames);

  const handle        = e => setForm(p => ({ ...p, [e.target.name]: e.target.value }));
  const handleEdit    = e => setEditForm(p => ({ ...p, [e.target.name]: e.target.value }));
  const handleBilling = e => setBillingForm(p => ({ ...p, [e.target.name]: e.target.value }));

  // ── Open edit dialog pre-filled ──────────────────────────────────────────
  const openEdit = (originalIdx) => {
    const eng = history[originalIdx];
    if (!eng) return;
    setDialogErr("");
    setEditForm({
      client_name:      eng.client_name      || "",
      project_name:     eng.project_name     || "",
      role:             eng.role             || "",
      start_date:       eng.start_date       ? String(eng.start_date).split("T")[0]  : "",
      end_date:         eng.end_date         ? String(eng.end_date).split("T")[0]    : "",
      billing_rate:     eng.billing_rate     || "",
      billing_currency: eng.billing_currency || "INR",
      work_location:    eng.work_location    || "",
      technology:       eng.technology       || "",
      notes:            eng.notes            || "",
    });
    setEditIdx(originalIdx);
    setEditOpen(true);
  };

  const handleEditSave = async () => {
    if (saving || !editForm.client_name) return;
    setSaving(true); setDialogErr("");
    try {
      await fetch(`${EMPLOYEE_BASE}/${employee._id}/engagement/${editIdx}`, {
        method: "PUT", headers: hdrs(),
        body: JSON.stringify({
          ...editForm,
          billing_rate: editForm.billing_rate ? Number(editForm.billing_rate) : 0,
        }),
      }).then(ok);
      onRefresh(); setEditOpen(false);
    } catch (e) {
      setDialogErr(errText(e, "Could not save engagement"));
    } finally { setSaving(false); }
  };

  // ── Add new engagement ───────────────────────────────────────────────────
  const handleSave = async () => {
    if (saving || !form.client_name) return;
    setSaving(true); setDialogErr("");
    try {
      await api.addEngagement(employee._id, { ...form, billing_rate: form.billing_rate ? Number(form.billing_rate) : 0 });
      onRefresh(); setOpen(false); setForm(EMPTY_ENG);
    } catch (e) {
      setDialogErr(errText(e, "Could not add engagement"));
    } finally { setSaving(false); }
  };

  const handleEnd = async (idx) => {
    if (ending !== null) return;
    if (!window.confirm(`Mark the ${history[idx]?.client_name || ""} engagement as ended today?`)) return;
    setEnding(idx); setErr("");
    try {
      await api.endEngagement(employee._id, idx);
      await onRefresh();
    } catch (e) {
      setErr(errText(e, "Could not end engagement"));
    } finally { setEnding(null); }
  };

  // ── Billing rate update ──────────────────────────────────────────────────
  const openBillingUpdate = (idx) => {
    const eng = history[idx];
    if (!eng) return;
    setDialogErr("");
    setBillingForm({
      billing_rate:     eng.billing_rate || "",
      billing_currency: eng.billing_currency || "INR",
      effective_from:   new Date().toISOString().split("T")[0],
      note:             "",
    });
    setBillingIdx(idx);
    setBillingOpen(true);
  };

  const handleBillingUpdate = async () => {
    if (saving || !billingForm.billing_rate) return;
    setSaving(true); setDialogErr("");
    try {
      await fetch(`${EMPLOYEE_BASE}/${employee._id}/engagement/${billingIdx}/billing`, {
        method: "POST", headers: hdrs(),
        body: JSON.stringify({ ...billingForm, billing_rate: Number(billingForm.billing_rate) }),
      }).then(ok);
      onRefresh(); setBillingOpen(false);
    } catch (e) {
      setDialogErr(errText(e, "Could not update billing rate"));
    } finally { setSaving(false); }
  };

  return (
    <Box p={3}>
      {err && <Alert severity="error" onClose={() => setErr("")} sx={{ mb: 2 }}>{err}</Alert>}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <SectionLabel>Client Engagements ({history.length})</SectionLabel>
        <Button size="small" variant="outlined" startIcon={<Add />} onClick={() => { setDialogErr(""); setOpen(true); }}
          sx={{ ...btnSx, height: CONTROL_HEIGHT.sm, borderColor: COLORS.primary, color: COLORS.primary, mb: 1.5 }}>
          Add Engagement
        </Button>
      </Box>

      {history.length === 0 ? (
        <Box display="flex" flexDirection="column" alignItems="center" py={6} gap={1}>
          <Timeline sx={{ fontSize: 48, color: COLORS.faint }} />
          <Typography color="text.secondary" fontWeight={600}>No client history yet</Typography>
          <Typography fontSize={13} color="text.disabled">Add the first client engagement above.</Typography>
        </Box>
      ) : (
        [...history].reverse().map((eng, revIdx) => {
          const originalIdx = history.length - 1 - revIdx;
          const isActive    = !eng.end_date;
          const billingHist = eng.billing_history || [];

          return (
            <Box key={revIdx} display="flex" gap={2} mb={3}>
              {/* Timeline dot */}
              <Box display="flex" flexDirection="column" alignItems="center" sx={{ pt: 0.5, minWidth: 24 }}>
                <Box sx={{
                  width: 14, height: 14, borderRadius: "50%", flexShrink: 0,
                  bgcolor: isActive ? COLORS.success : dc,
                  border: "3px solid", borderColor: isActive ? "#bbf7d0" : COLORS.border,
                  boxShadow: isActive ? "0 0 0 3px #dcfce7" : "none",
                }} />
                {revIdx < history.length - 1 && (
                  <Box sx={{ width: 2, flexGrow: 1, minHeight: 40, bgcolor: COLORS.border, my: 0.5 }} />
                )}
              </Box>

              {/* Card */}
              <Box flex={1} p={2} borderRadius={2}
                sx={{ border: `1px solid ${isActive ? "#bbf7d0" : COLORS.border}`,
                      bgcolor: isActive ? COLORS.successSoft : COLORS.surfaceTint }}>

                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1.5}>
                  <Box>
                    <Box display="flex" alignItems="center" gap={1} mb={0.3}>
                      <Typography fontWeight={800} fontSize={TYPE.cardTitle} color={isActive ? COLORS.success : COLORS.primaryDeep}>
                        {eng.client_name}
                      </Typography>
                      {isActive && <Chip label="Current" size="small" color="success"
                        sx={{ height: 18, fontSize: TYPE.overline, fontWeight: 700 }} />}
                    </Box>
                    <Typography fontSize={13} color="text.secondary">
                      {eng.project_name && `${eng.project_name} · `}{eng.role || "—"}
                    </Typography>
                  </Box>

                  {/* Action buttons */}
                  <Box display="flex" gap={0.8} flexWrap="wrap" justifyContent="flex-end">
                    <Tooltip title="Edit engagement details">
                      <Button size="small" variant="outlined"
                        onClick={() => openEdit(originalIdx)}
                        sx={{ ...smallBtnSx,
                              borderColor: COLORS.faint, color: COLORS.muted,
                              "&:hover": { borderColor: COLORS.primary, color: COLORS.primary } }}>
                        Edit
                      </Button>
                    </Tooltip>
                    {isActive && (
                      <>
                        <Tooltip title="Update billing rate with history">
                          <Button size="small" variant="outlined" color="info"
                            onClick={() => openBillingUpdate(originalIdx)}
                            sx={smallBtnSx}>
                            Update Rate
                          </Button>
                        </Tooltip>
                        <Tooltip title="Mark as Ended">
                          <Button size="small" variant="outlined" color="warning"
                            disabled={ending !== null}
                            onClick={() => handleEnd(originalIdx)}
                            sx={smallBtnSx}>
                            End
                          </Button>
                        </Tooltip>
                      </>
                    )}
                  </Box>
                </Box>

                {/* Details */}
                <Grid container spacing={1.5} mb={1}>
                  {[
                    ["Start Date",    fmtDate(eng.start_date)],
                    ["End Date",      eng.end_date ? fmtDate(eng.end_date) : "Present"],
                    ["Current Rate",  fmtMoney(eng.billing_rate, eng.billing_currency)],
                    ...(eng.work_location ? [["Location", eng.work_location]] : []),
                  ].map(([label, val]) => (
                    <Grid item xs={6} sm={3} key={label}>
                      <Typography sx={fieldLabelSx}>{label}</Typography>
                      <Typography fontWeight={600} fontSize={12}>{val}</Typography>
                    </Grid>
                  ))}
                </Grid>

                {/* Billing history */}
                {billingHist.length > 0 && (
                  <Box mt={1.5} p={1.5} borderRadius={1.5}
                    sx={{ bgcolor: isActive ? COLORS.successSoft : COLORS.surfaceTint,
                          border: "1px solid", borderColor: isActive ? "#bbf7d0" : COLORS.border }}>
                    <Typography fontSize={TYPE.overline} fontWeight={700} color="text.secondary"
                      textTransform="uppercase" letterSpacing="0.06em" mb={1}>
                      Billing Rate History
                    </Typography>
                    {billingHist.map((bh, bi) => (
                      <Box key={bi} display="flex" alignItems="center" gap={2} py={0.5}
                        sx={{ borderBottom: bi < billingHist.length - 1 ? `1px dashed ${COLORS.border}` : "none" }}>
                        <Typography fontSize={12} fontWeight={700}
                          color={bi === billingHist.length - 1 ? COLORS.success : "text.primary"}>
                          {fmtMoney(bh.rate, bh.currency)}
                        </Typography>
                        <Typography fontSize={11} color="text.secondary">from {fmtDate(bh.effective_from)}</Typography>
                        {bh.note && <Typography fontSize={11} color="text.disabled">· {bh.note}</Typography>}
                        {bi === billingHist.length - 1 && (
                          <Chip label="Current" size="small"
                            sx={{ height: 18, fontSize: TYPE.overline, fontWeight: 700, bgcolor: COLORS.successSoft, color: COLORS.success }} />
                        )}
                      </Box>
                    ))}
                  </Box>
                )}

                {eng.technology && (
                  <Box mt={1.5} display="flex" flexWrap="wrap" gap={0.5}>
                    {String(eng.technology).split(",").map(t => t.trim()).filter(Boolean).map(t => (
                      <Chip key={t} label={t.trim()} size="small"
                        sx={{ fontSize: TYPE.overline, height: 18,
                              bgcolor: isActive ? COLORS.successSoft : COLORS.border,
                              color:   isActive ? COLORS.success : COLORS.muted }} />
                    ))}
                  </Box>
                )}
                {eng.notes && <Typography fontSize={11} color="text.secondary" mt={1}>{eng.notes}</Typography>}
              </Box>
            </Box>
          );
        })
      )}

      {/* ── Add Engagement Dialog ─────────────────────────────────────── */}
      <Dialog open={open} onClose={() => { setOpen(false); setForm(EMPTY_ENG); }} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ borderBottom: `1px solid ${COLORS.border}` }}>
          Add Client Engagement
          <Typography fontSize={12} color="text.secondary">for {employee.name}</Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: "20px !important" }}>
          {dialogErr && <Alert severity="error" sx={{ mb: 2 }}>{dialogErr}</Alert>}
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField select fullWidth size="small" required label="Client Name" name="client_name"
                value={form.client_name} onChange={handle}>
                <MenuItem value="">— Select Client —</MenuItem>
                {clientNames.map(name => <MenuItem key={name} value={name}>{name}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label="Project Name" name="project_name" value={form.project_name} onChange={handle} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label="Role on Project" name="role" value={form.role} onChange={handle} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" type="date" label="Start Date" name="start_date"
                value={form.start_date} onChange={handle} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" type="date" label="End Date (blank = current)"
                name="end_date" value={form.end_date} onChange={handle} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" type="number" label="Billing Rate" name="billing_rate" value={form.billing_rate} onChange={handle} />
            </Grid>
            <Grid item xs={6}>
              <TextField select fullWidth size="small" label="Currency" name="billing_currency" value={form.billing_currency} onChange={handle}>
                {CURRENCIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label="Work Location" name="work_location" value={form.work_location} onChange={handle} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label="Technology Stack (comma-sep)" name="technology" value={form.technology} onChange={handle} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline rows={2} size="small" label="Notes" name="notes" value={form.notes} onChange={handle} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${COLORS.border}` }}>
          <Button onClick={() => { setOpen(false); setForm(EMPTY_ENG); }} disabled={saving} sx={{ ...btnSx, color: COLORS.muted }}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving || !form.client_name}
            sx={primaryBtnSx}>
            {saving && <CircularProgress size={16} sx={{ mr: 1, color: "#fff" }} />}
            Save Engagement
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Edit Engagement Dialog ────────────────────────────────────── */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ borderBottom: `1px solid ${COLORS.border}` }}>
          Edit Engagement
          {editIdx !== null && history[editIdx] && (
            <Typography fontSize={12} color="text.secondary">{history[editIdx].client_name}</Typography>
          )}
        </DialogTitle>
        <DialogContent sx={{ pt: "20px !important" }}>
          {dialogErr && <Alert severity="error" sx={{ mb: 2 }}>{dialogErr}</Alert>}
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField select fullWidth size="small" required label="Client Name" name="client_name"
                value={editForm.client_name || ""} onChange={handleEdit}>
                <MenuItem value="">— Select Client —</MenuItem>
                {withClient(editForm.client_name).map(name => <MenuItem key={name} value={name}>{name}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label="Project Name" name="project_name"
                value={editForm.project_name || ""} onChange={handleEdit} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label="Role on Project" name="role"
                value={editForm.role || ""} onChange={handleEdit} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" type="date" label="Start Date" name="start_date"
                value={editForm.start_date || ""} onChange={handleEdit} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" type="date" label="End Date (blank = active)"
                name="end_date" value={editForm.end_date || ""} onChange={handleEdit} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" type="number" label="Billing Rate" name="billing_rate"
                value={editForm.billing_rate || ""} onChange={handleEdit} />
            </Grid>
            <Grid item xs={6}>
              <TextField select fullWidth size="small" label="Currency" name="billing_currency"
                value={editForm.billing_currency || "INR"} onChange={handleEdit}>
                {CURRENCIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label="Work Location" name="work_location"
                value={editForm.work_location || ""} onChange={handleEdit} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label="Technology Stack (comma-sep)" name="technology"
                value={editForm.technology || ""} onChange={handleEdit} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline rows={2} size="small" label="Notes" name="notes"
                value={editForm.notes || ""} onChange={handleEdit} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${COLORS.border}` }}>
          <Button onClick={() => setEditOpen(false)} disabled={saving} sx={{ ...btnSx, color: COLORS.muted }}>Cancel</Button>
          <Button variant="contained" onClick={handleEditSave}
            disabled={saving || !editForm.client_name}
            sx={primaryBtnSx}>
            {saving && <CircularProgress size={16} sx={{ mr: 1, color: "#fff" }} />}
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Update Billing Rate Dialog ────────────────────────────────── */}
      <Dialog open={billingOpen} onClose={() => setBillingOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ borderBottom: `1px solid ${COLORS.border}` }}>
          Update Billing Rate
          {billingIdx !== null && history[billingIdx] && (
            <Typography fontSize={12} color="text.secondary">
              {history[billingIdx].client_name} · Current: {fmtMoney(history[billingIdx].billing_rate, history[billingIdx].billing_currency)}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent sx={{ pt: "20px !important" }}>
          {dialogErr && <Alert severity="error" sx={{ mb: 2 }}>{dialogErr}</Alert>}
          <Grid container spacing={2}>
            <Grid item xs={8}>
              <TextField fullWidth size="small" type="number" required label="New Billing Rate"
                name="billing_rate" value={billingForm.billing_rate} onChange={handleBilling} />
            </Grid>
            <Grid item xs={4}>
              <TextField select fullWidth size="small" label="Currency" name="billing_currency"
                value={billingForm.billing_currency} onChange={handleBilling}>
                {CURRENCIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" type="date" label="Effective From"
                name="effective_from" value={billingForm.effective_from} onChange={handleBilling}
                InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Note (e.g. Annual increment)"
                name="note" value={billingForm.note} onChange={handleBilling}
                placeholder="e.g. Annual increment, Revised contract…" />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${COLORS.border}` }}>
          <Button onClick={() => setBillingOpen(false)} disabled={saving} sx={{ ...btnSx, color: COLORS.muted }}>Cancel</Button>
          <Button variant="contained" onClick={handleBillingUpdate}
            disabled={saving || !billingForm.billing_rate}
            sx={{ ...btnSx, bgcolor: COLORS.success, "&:hover": { bgcolor: COLORS.success, filter: "brightness(0.9)" } }}>
            {saving && <CircularProgress size={16} sx={{ mr: 1, color: "#fff" }} />}
            Save Rate Change
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}







// TAB 4 — Finanicial Summary 


function LifecycleTab({ employee, onRefresh }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [salaryOpen, setSalaryOpen] = useState(false);
  const [salaryForm, setSalaryForm] = useState({ salary: "", effective_from: "", note: "" });
  const [saving, setSaving] = useState(false);
  const [loadErr, setLoadErr] = useState("");
  const [salaryErr, setSalaryErr] = useState("");

  const load = async () => {
    setLoading(true); setLoadErr("");
    try { setData((await api.getLifecycle(employee._id))?.data || null); }
    catch (e) { setLoadErr(errText(e, "Could not load the financial summary")); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [employee._id]);

  const openSalaryUpdate = () => {
    setSalaryForm({
      salary: employee.salary || "",
      effective_from: new Date().toISOString().split("T")[0],
      note: "",
    });
    setSalaryErr("");
    setSalaryOpen(true);
  };

  const handleSalarySave = async () => {
    if (saving || !salaryForm.salary) return;
    setSaving(true); setSalaryErr("");
    try {
      await api.updateSalary(employee._id, { ...salaryForm, salary: Number(salaryForm.salary) });
      setSalaryOpen(false); load(); onRefresh();
    } catch (e) {
      setSalaryErr(errText(e, "Could not record salary change"));
    } finally { setSaving(false); }
  };
  const fmtTenure = (years) => {
    const totalMonths = Math.round((Number(years) || 0) * 12);
    const yrs = Math.floor(totalMonths / 12);
    const mos = totalMonths % 12;
    if (yrs === 0) return `${mos} mo`;
    if (mos === 0) return `${yrs} yr`;
    return `${yrs} yr ${mos} mo`;
  };
  if (loading && !data) return (
    <Box p={4} display="flex" flexDirection="column" alignItems="center" gap={1.5}>
      <CircularProgress size={28} sx={{ color: COLORS.primary }} />
      <Typography fontSize={TYPE.body} color={COLORS.muted}>Loading financial summary…</Typography>
    </Box>
  );
  if (!data) return (
    <Box p={4} display="flex" flexDirection="column" alignItems="center" gap={1.5} textAlign="center">
      <Typography fontSize={TYPE.body} color={loadErr ? COLORS.error : COLORS.muted}>
        {loadErr || "No financial data available for this employee."}
      </Typography>
      <Button variant="outlined" onClick={load} sx={btnSx}>Retry</Button>
    </Box>
  );
  const engagements      = data.engagements       || [];
  const revenueByClient  = data.revenue_by_client || [];
  const benchPeriods     = data.bench_periods     || [];
  const salaryHistory    = data.salary_history    || [];
  const salaryIncrements = data.salary_increments || [];

  return (
    <Box p={3}>
      <Grid container spacing={2} mb={3}>
        <Grid item xs={6} sm={3}>
          <Box p={2} height="100%" boxSizing="border-box" borderRadius={`${RADIUS.md}px`} border={`1px solid ${COLORS.border}`}>
            <Typography sx={fieldLabelSx}>Joined</Typography>
            <Typography fontWeight={800} fontSize={TYPE.cardTitle} sx={{ fontVariantNumeric: "tabular-nums" }}>
              {fmtDate(data.date_of_joining)}
            </Typography>
            {/* Change this line: */}
            <Typography fontSize={11} color="text.secondary">
              {fmtTenure(data.tenure_years)} tenure
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Box p={2} height="100%" boxSizing="border-box" borderRadius={`${RADIUS.md}px`} border="1px solid #fed7aa" bgcolor={COLORS.warningSoft}>
            <Typography sx={fieldLabelSx}>Total Bench Time</Typography>
            <Typography fontWeight={800} fontSize={TYPE.cardTitle} sx={{ fontVariantNumeric: "tabular-nums" }} color={COLORS.warning}>{data.total_bench_days ?? 0} days</Typography>
          </Box>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Box p={2} height="100%" boxSizing="border-box" borderRadius={`${RADIUS.md}px`} border="1px solid #bbf7d0" bgcolor={COLORS.successSoft}>
            <Typography sx={fieldLabelSx}>Revenue Generated</Typography>
            <Typography fontWeight={800} fontSize={TYPE.cardTitle} sx={{ fontVariantNumeric: "tabular-nums" }} color={COLORS.success}>{fmtMoney(data.total_revenue_generated)}</Typography>
          </Box>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Box p={2} height="100%" boxSizing="border-box" borderRadius={`${RADIUS.md}px`} border="1px solid #fecaca" bgcolor={COLORS.errorSoft}>
            <Typography sx={fieldLabelSx}>Total Salary Paid</Typography>
            <Typography fontWeight={800} fontSize={TYPE.cardTitle} sx={{ fontVariantNumeric: "tabular-nums" }} color={COLORS.error}>{fmtMoney(data.total_salary_paid)}</Typography>
          </Box>
        </Grid>
      </Grid>

      <SectionLabel>Revenue by Client</SectionLabel>
      <Table size="small" sx={{ mb: 3 }}>
        <TableHead><TableRow>
          <TableCell sx={headSx}>Client</TableCell><TableCell sx={headSx}>Project / Role</TableCell>
          <TableCell sx={headSx}>Period</TableCell><TableCell align="right" sx={headSx}>Revenue</TableCell>
        </TableRow></TableHead>
        <TableBody>
          {engagements.length === 0 && revenueByClient.length === 0 && (
            <TableRow>
              <TableCell colSpan={4} align="center" sx={{ color: COLORS.muted, py: 3 }}>No client engagements yet.</TableCell>
            </TableRow>
          )}
          {engagements.map((e, i) => (
            <TableRow key={i}>
              <TableCell>{e.client_name}</TableCell>
              <TableCell>{[e.project_name, e.role].filter(Boolean).join(" · ") || "—"}</TableCell>
              <TableCell sx={{ whiteSpace: "nowrap" }}>{fmtDate(e.start_date)} → {e.end_date ? fmtDate(e.end_date) : "Present"}</TableCell>
              <TableCell align="right" sx={{ whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>{fmtMoney(e.revenue_generated)}</TableCell>
            </TableRow>
          ))}
          {revenueByClient.map((c, i) => (
            <TableRow key={`tot-${i}`} sx={{ bgcolor: COLORS.surfaceTint }}>
              <TableCell colSpan={3}><Typography fontWeight={700}>{c.client_name} — Total</Typography></TableCell>
              <TableCell align="right"><Typography fontWeight={700} color={COLORS.success}>{fmtMoney(c.total_revenue)}</Typography></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <SectionLabel>Bench Periods</SectionLabel>
      {benchPeriods.length === 0 ? (
        <Typography fontSize={13} color="text.secondary" mb={3}>No bench gaps — continuously deployed since joining.</Typography>
      ) : (
        <Table size="small" sx={{ mb: 3 }}>
          <TableHead><TableRow><TableCell sx={headSx}>From</TableCell><TableCell sx={headSx}>To</TableCell><TableCell align="right" sx={headSx}>Days</TableCell></TableRow></TableHead>
          <TableBody>
            {benchPeriods.map((p, i) => (
              <TableRow key={i}>
                <TableCell>{fmtDate(p.start)}</TableCell>
                <TableCell>{p.ongoing ? "Present" : fmtDate(p.end)}</TableCell>
                <TableCell align="right">{p.days}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
{/* ── Salary History ── */}
<Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
  <SectionLabel>Salary History</SectionLabel>
  <Button
    size="small" variant="outlined"
    onClick={openSalaryUpdate}
    sx={{ ...btnSx, height: CONTROL_HEIGHT.sm, borderColor: COLORS.primary, color: COLORS.primary, mb: 1.5 }}>
    Record Salary Change
  </Button>
</Box>

<Box sx={{ border: `1px solid ${COLORS.border}`, borderRadius: 2, overflow: "hidden", mb: 3 }}>
  <Table size="small">
    <TableHead>
      <TableRow sx={{ bgcolor: COLORS.surfaceTint }}>
        <TableCell sx={headSx}>
          Effective From
        </TableCell>
        <TableCell align="right" sx={headSx}>
          Annual Salary
        </TableCell>
        <TableCell align="right" sx={headSx}>
          Monthly
        </TableCell>
        <TableCell align="right" sx={headSx}>
          Increase
        </TableCell>
        <TableCell align="right" sx={headSx}>
          Hike %
        </TableCell>
        <TableCell sx={headSx}>
          Note
        </TableCell>
      </TableRow>
    </TableHead>
    <TableBody>
      {salaryHistory.length === 0 && (
        <TableRow>
          <TableCell colSpan={6} align="center" sx={{ color: COLORS.muted, py: 3 }}>No salary records yet.</TableCell>
        </TableRow>
      )}
      {salaryHistory.map((s, i) => {
        const inc = salaryIncrements.find(
          x => x.effective_from === s.effective_from
        );
        const isLatest = i === salaryHistory.length - 1;
        const isFirst  = i === 0;

        return (
          <TableRow key={i}
            sx={{ bgcolor: isLatest ? COLORS.successSoft : "inherit" }}>

            {/* Effective From */}
            <TableCell>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography fontSize={13} fontWeight={600}>
                  {fmtDate(s.effective_from)}
                </Typography>
                {isFirst && (
                  <Chip label="Joining" size="small"
                    sx={{ fontSize: TYPE.overline, height: 18, bgcolor: COLORS.accentSoft, color: COLORS.primary, fontWeight: 700 }} />
                )}
                {isLatest && !isFirst && (
                  <Chip label="Current" size="small"
                    sx={{ fontSize: TYPE.overline, height: 18, bgcolor: COLORS.successSoft, color: COLORS.success, fontWeight: 700 }} />
                )}
              </Box>
            </TableCell>

            {/* Annual Salary */}
            <TableCell align="right">
              <Typography fontSize={13} fontWeight={700}
                color={isLatest ? COLORS.success : COLORS.primaryDeep}>
                {fmtMoney(s.rate)}
              </Typography>
            </TableCell>

            {/* Monthly */}
            <TableCell align="right">
              <Typography fontSize={12} color={COLORS.muted}>
                {fmtMoney(Math.round((s.rate || 0) / 12))}
                <Typography component="span" fontSize={TYPE.overline} color={COLORS.faint}>/mo</Typography>
              </Typography>
            </TableCell>

            {/* Increase Amount */}
            <TableCell align="right">
              {inc ? (
                <Typography fontSize={12} fontWeight={700}
                  color={inc.increase_amount >= 0 ? COLORS.success : COLORS.error}>
                  {inc.increase_amount >= 0 ? "+" : ""}
                  {fmtMoney(inc.increase_amount)}
                </Typography>
              ) : (
                <Typography fontSize={12} color={COLORS.faint}>—</Typography>
              )}
            </TableCell>

            {/* Hike % */}
            <TableCell align="right">
              {inc ? (
                <Chip
                  label={`${inc.increase_amount >= 0 ? "+" : ""}${inc.increase_pct}%`}
                  size="small"
                  sx={{
                    fontWeight: 700, fontSize: 11,
                    bgcolor: inc.increase_pct >= 10 ? COLORS.successSoft
                           : inc.increase_pct >= 0  ? "#fef3c7" : "#fee2e2",
                    color:   inc.increase_pct >= 10 ? COLORS.success
                           : inc.increase_pct >= 0  ? "#d97706" : COLORS.error,
                  }}
                />
              ) : (
                <Typography fontSize={12} color={COLORS.faint}>—</Typography>
              )}
            </TableCell>

            {/* Note */}
            <TableCell>
              <Typography fontSize={12} color={COLORS.muted}>
                {s.note || "—"}
              </Typography>
            </TableCell>
          </TableRow>
        );
      })}

    {/* ── Summary footer row ── */}
    <TableRow sx={{ bgcolor: COLORS.accentSoft, borderTop: `2px solid ${COLORS.border}` }}>
      <TableCell colSpan={2}>
        <Typography fontSize={12} fontWeight={800} color={COLORS.primaryDeep}>
          Total Salary Paid (prorated)
        </Typography>
      </TableCell>
      <TableCell colSpan={3} />
      <TableCell align="right" colSpan={1}>
        <Typography fontSize={13} fontWeight={800} color={COLORS.error}>
          {fmtMoney(data.total_salary_paid)}
        </Typography>
      </TableCell>
    </TableRow>
    </TableBody>
  </Table>
</Box>

{/* ── Net Contribution box ── */}
<Box p={2} borderRadius={2} mb={3}
  sx={{
    border: `1px solid ${data.net_contribution >= 0 ? "#bbf7d0" : "#fecaca"}`,
    bgcolor: data.net_contribution >= 0 ? COLORS.successSoft : COLORS.errorSoft,
  }}>
  <Box display="flex" justifyContent="space-between" alignItems="center">
    <Box>
      <Typography fontSize={11} fontWeight={700} color={COLORS.muted}
        textTransform="uppercase" letterSpacing="0.06em">
        Net Contribution (Revenue − Salary)
      </Typography>
      <Typography fontSize={11} color={COLORS.faint} mt={0.3}>
        What this employee has contributed net of their cost
      </Typography>
    </Box>
    <Typography fontSize={TYPE.section} fontWeight={800} sx={{ fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}
      color={data.net_contribution >= 0 ? COLORS.success : COLORS.error}>
      {data.net_contribution >= 0 ? "+" : ""}
      {fmtMoney(data.net_contribution)}
    </Typography>
  </Box>
</Box>

      <Dialog open={salaryOpen} onClose={() => setSalaryOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Record Salary Change</DialogTitle>
        <DialogContent sx={{ pt: "8px !important" }}>
          {salaryErr && <Alert severity="error" sx={{ mb: 2 }}>{salaryErr}</Alert>}
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField fullWidth size="small" type="number" label="New Annual Salary (₹)"
                value={salaryForm.salary} onChange={e => setSalaryForm(p => ({ ...p, salary: e.target.value }))} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" type="date" label="Effective From" InputLabelProps={{ shrink: true }}
                value={salaryForm.effective_from} onChange={e => setSalaryForm(p => ({ ...p, effective_from: e.target.value }))} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Note (e.g. Annual increment)"
                value={salaryForm.note} onChange={e => setSalaryForm(p => ({ ...p, note: e.target.value }))} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setSalaryOpen(false)} disabled={saving} sx={{ ...btnSx, color: COLORS.muted }}>Cancel</Button>
          <Button variant="contained" onClick={handleSalarySave} disabled={saving || !salaryForm.salary} sx={{ ...btnSx, bgcolor: COLORS.success, "&:hover": { bgcolor: COLORS.success, filter: "brightness(0.9)" } }}>
            {saving && <CircularProgress size={16} sx={{ mr: 1, color: "#fff" }} />}
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}



// Main EmployeeDetail dialog — assembles all tabs
const TABS = ["Profile", "Onboarding", "Documents", "Client History","Financial Summary"];

export default function EmployeeDetail({ open, employee, onClose, onEdit, onEmployeeUpdate }) {
  const [tab,        setTab]        = useState(0);
  const [onboarding, setOnboarding] = useState(null);
  const [obLoading,  setObLoading]  = useState(false);
  const [exporting,  setExporting]  = useState(false);
  const [exportErr,  setExportErr]  = useState("");
  const [obError,    setObError]    = useState("");

  // Refreshes keep the current tab mounted; only an empty state shows the spinner.
  const loadOnboarding = async () => {
    if (!employee) return;
    setObLoading(true); setObError("");
    try {
      const res = await api.getOnboarding(employee._id);
      setOnboarding(res?.data || null);
    } catch (e) {
      setObError(errText(e, "Could not load onboarding data"));
    } finally {
      setObLoading(false);
    }
  };

  useEffect(() => {
    if (open && employee) { setTab(0); setOnboarding(null); loadOnboarding(); setExportErr(""); }
  }, [open, employee?._id]);

  const handleExport = async () => {
    if (exporting) return;
    setExporting(true); setExportErr("");
    try {
      const safeName = `${employee.emp_id || "employee"}_${String(employee.name || "").replace(/\s+/g, "_")}_profile_${new Date().toISOString().slice(0,10)}.xlsx`;
      await api.exportEmployee(employee._id, safeName);
    } catch {
      setExportErr("Export failed. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  if (!employee) return null;
  const dc = DEPT_COLOR[employee.department] || "#475569";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
      PaperProps={{ sx: { minHeight: "82vh", borderRadius: `${RADIUS.lg}px`, fontFamily: FONT_BODY, color: COLORS.ink } }}>

      {/* ── Dialog Header (sticky) ─────────────────────────────────────── */}
      <DialogTitle sx={{ p: 0, borderBottom: `1px solid ${COLORS.border}` }}>
        <Box sx={{ px: 3, pt: 3 }}>
          <Box display="flex" alignItems="flex-start" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={2}>
              <Avatar sx={{ width: 56, height: 56, bgcolor: dc, fontSize: TYPE.section, fontWeight: 700, fontFamily: FONT_BODY }}>
                {nameInitials(employee.name)}
              </Avatar>
              <Box minWidth={0}>
                <Typography sx={{ fontFamily: FONT_DISPLAY, fontSize: TYPE.section, fontWeight: 700, color: COLORS.primaryDeep, lineHeight: 1.25, ...ELLIPSIS }} title={employee.name}>
                  {employee.name}
                </Typography>
                <Typography color={COLORS.muted} fontSize={TYPE.small}>
                  {[employee.designation, employee.department].filter(Boolean).join(" · ") || "—"}
                </Typography>
                <Box display="flex" gap={1} mt={0.6} flexWrap="wrap">
                  {employee.status && <Chip label={humanizeStatus(employee.status)} color={STATUS_COLOR[employee.status] || "default"}
                    size="small" />}
                  {employee.emp_id && <Chip label={employee.emp_id} size="small" variant="outlined" />}
                  {employee.employment_type && <Chip label={employee.employment_type} size="small"
                    sx={{ bgcolor: COLORS.accentSoft, color: COLORS.primaryDark }} />}
                  {employee.current_client && (
                    <Chip label={`Client: ${employee.current_client}`} size="small"
                      color="info" sx={{ fontWeight: 600 }} />
                  )}
                </Box>
              </Box>
            </Box>
            <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
          </Box>

          {/* Tab Bar */}
          <Box display="flex" mt={2}>
            {TABS.map((label, i) => (
              <Box key={i} onClick={() => setTab(i)} role="tab" aria-selected={tab === i} sx={{
                px: 2, py: 1.5, cursor: "pointer", fontSize: TYPE.small, fontFamily: FONT_BODY,
                fontWeight: tab === i ? 700 : 600, whiteSpace: "nowrap",
                borderBottom: tab === i ? `2px solid ${COLORS.primary}` : "2px solid transparent",
                color: tab === i ? COLORS.primary : COLORS.muted,
                transition: "all 0.15s",
                userSelect: "none",
              }}>
                {label}
              </Box>
            ))}
          </Box>
        </Box>
      </DialogTitle>

      {/* ── Tab Content ──────────────────────────────────────────────────── */}
      <DialogContent sx={{ p: 0 }}>
        {(tab === 1 || tab === 2) && !onboarding ? (
          <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight={300} gap={1.5} px={3} textAlign="center">
            {obLoading || !obError ? (
              <>
                <CircularProgress size={28} sx={{ color: COLORS.primary }} />
                <Typography fontSize={TYPE.body} color={COLORS.muted}>Loading onboarding data…</Typography>
              </>
            ) : (
              <>
                <Typography fontSize={TYPE.body} color={COLORS.error}>{obError}</Typography>
                <Button variant="outlined" onClick={loadOnboarding} sx={btnSx}>Retry</Button>
              </>
            )}
          </Box>
        ) : (
          <>
            {exportErr && (
              <Box px={3} pt={2}>
                <Alert severity="error" onClose={() => setExportErr("")}>{exportErr}</Alert>
              </Box>
            )}
            {obError && onboarding && (tab === 1 || tab === 2) && (
              <Box px={3} pt={2}>
                <Alert severity="error" onClose={() => setObError("")}
                  action={<Button size="small" onClick={loadOnboarding} sx={{ textTransform: "none", fontWeight: 600 }}>Retry</Button>}>
                  {obError}
                </Alert>
              </Box>
            )}
            {tab === 0 && <ProfileTab    employee={employee} />}
            {tab === 1 && <OnboardingTab employee={employee} onboarding={onboarding} onRefresh={loadOnboarding} />}
            {tab === 2 && <DocumentsTab  employee={employee} onboarding={onboarding} onRefresh={loadOnboarding} />}
            {tab === 3 && <EngagementTab employee={employee} onRefresh={onEmployeeUpdate} />}
            {tab === 4 && <LifecycleTab employee={employee} onRefresh={onEmployeeUpdate} />}
          </>
        )}
      </DialogContent>

      {/* ── Footer Actions ────────────────────────────────────────────────── */}
      <DialogActions sx={{ px: 3, pb: 2.5, borderTop: `1px solid ${COLORS.border}`, display: "flex", justifyContent: "space-between" }}>
        {/* Left — Export */}
        <Tooltip title="Download full profile as Excel">
          <span>
            <Button
              variant="outlined"
              startIcon={exporting ? <CircularProgress size={14} /> : <FileDownload />}
              onClick={handleExport}
              disabled={exporting}
              sx={{
                ...btnSx,
                borderColor: COLORS.success, color: COLORS.success,
                "&:hover": { borderColor: COLORS.success, bgcolor: COLORS.successSoft },
              }}>
              {exporting ? "Exporting…" : "Export Profile"}
            </Button>
          </span>
        </Tooltip>

        {/* Right — Close / Edit */}
        <Box display="flex" gap={1}>
          <Button onClick={onClose} sx={{ ...btnSx, color: COLORS.muted }}>Close</Button>
          <Button variant="contained" onClick={() => { onClose(); onEdit(employee); }}
            sx={primaryBtnSx}>
            Edit Employee
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}